import { useState } from "react";
import { TableCell, TableSortLabel } from "@mui/material";

/**
 * Ordenar las tablas de los reportes por cualquier columna.
 *
 * Vive en un solo lugar y no copiado en cada reporte para que todas las tablas se
 * comporten igual: el mismo criterio al estrenar una columna, la misma flecha, y el mismo
 * detalle de que en papel el encabezado va plano —una flecha impresa no ordena nada y solo
 * confunde—.
 *
 * En los reportes agrupados por sucursal y gestor el orden se aplica dentro de cada grupo.
 * Mezclar los grupos romperia los totales que van debajo de cada uno, que son la mitad del
 * reporte.
 */

/**
 * La flecha de ordenar no se imprime.
 *
 * Varios reportes mandan a imprimir el mismo nodo que se ve en pantalla, asi que sin esto
 * la hoja saldria con una flecha al lado de una columna. En papel no ordena nada: solo
 * hace dudar de si significa algo. Se registra una sola vez para todos los reportes.
 */
const ESTILO_IMPRESION = "orden-tabla-impresion";

if (typeof document !== "undefined" && document.getElementById(ESTILO_IMPRESION) === null) {
  const estilo = document.createElement("style");
  estilo.id = ESTILO_IMPRESION;
  estilo.innerHTML =
    "@media print{.MuiTableSortLabel-icon{display:none !important}}";
  document.head.appendChild(estilo);
}

/**
 * @param campoInicial columna por la que sale ordenado antes de que nadie toque nada.
 * @param textos nombres de las columnas que se comparan como palabras y no como numeros.
 */
export const useOrdenTabla = (campoInicial, textos = []) => {
  const esTexto = (campo) => textos.includes(campo);

  const [orden, setOrden] = useState({
    campo: campoInicial,
    asc: esTexto(campoInicial),
  });

  const cambiarOrden = (campo) => {
    setOrden((actual) =>
      actual.campo === campo
        ? { campo, asc: !actual.asc }
        : // Al estrenar una columna: los textos de la A a la Z, y los numeros de mayor a
          // menor, que en estos reportes es lo que se va a buscar (el que mas debe, el que
          // mas se atrasa, el saldo mas alto).
          { campo, asc: esTexto(campo) }
    );
  };

  // Algunos reportes traen la fila anidada (row.result.fullName), asi que el campo puede
  // venir con punto.
  const valorDe = (fila, campo) =>
    campo.split(".").reduce((actual, parte) => actual?.[parte], fila);

  /** Devuelve una copia ordenada; nunca toca el arreglo original. */
  const ordenar = (filas) =>
    [...(filas ?? [])].sort((a, b) => {
      const signo = orden.asc ? 1 : -1;
      const izq = valorDe(a, orden.campo);
      const der = valorDe(b, orden.campo);

      if (esTexto(orden.campo)) {
        return signo * String(izq ?? "").localeCompare(String(der ?? ""));
      }

      // Las fechas llegan como texto ISO y no como numero; comparadas como texto quedan
      // igual en orden cronologico, que es lo que se espera de una columna de fecha.
      if (typeof izq === "string" || typeof der === "string") {
        return signo * String(izq ?? "").localeCompare(String(der ?? ""));
      }

      return signo * ((izq ?? 0) - (der ?? 0));
    });

  return { orden, cambiarOrden, ordenar };
};

/**
 * Encabezado de columna que ordena al hacerle clic. Recibe los mismos props que el
 * TableCell que reemplaza, para poder cambiarlo sin tocar los estilos de cada reporte.
 */
export const EncabezadoOrdenable = ({
  campo,
  orden,
  cambiarOrden,
  children,
  ...props
}) => (
  <TableCell
    {...props}
    sortDirection={orden.campo === campo ? (orden.asc ? "asc" : "desc") : false}
  >
    <TableSortLabel
      active={orden.campo === campo}
      direction={orden.asc ? "asc" : "desc"}
      onClick={() => cambiarOrden(campo)}
    >
      {children}
    </TableSortLabel>
  </TableCell>
);
